import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, Typography } from '@material-ui/core';
import styles from '../../styles.module.css';
import Button from '../../../components/Button/index.js';
import Notification from '../../../components/Notification/index.js';
import FormGroup from '../../../components/FormGroup/index.js';
import { ReactComponent as PasswordIcon } from '../../../svg/password.svg';
import { validateEmail } from '../../helpers.js';

function BasicSignIn({
  submit,
  warning,
  email,
  onBackToEmail,
  showForgotPassword,
  showTrustDevice,
}) {
  const [passwordError, setPasswordError] = useState('');
  const [backendError, setBackendError] = useState('');
  const [needHelpModalShow, setNeedHelpModalShow] = useState(false);

  const submitForm = useCallback(
    (event) => {
      event.preventDefault();

      const formData = new FormData(event.currentTarget);
      const emailData = formData.get('email').trim();
      const passwordData = formData.get('password').trim();
      const trustDevice = !!formData.get('trust_device');

      if (!passwordData) {
        setPasswordError('Please enter a password');
        return;
      }

      if (passwordData) {
        setPasswordError('');
      }

      const emailIsValid = validateEmail(emailData);

      if (emailData && emailIsValid && passwordData) {
        submit({
          email: emailData,
          password: passwordData,
          trustDevice,
          setError: setBackendError,
        });
      }
    },
    [submit],
  );

  if (warning) {
    return (
      <div
        style={{
          height: '15em',
          display: 'flex',
          'align-content': 'center',
          'flex-wrap': 'wrap',
        }}
      >
        <div className={styles.notification}>
          <Notification type="warning" message={warning} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.headerBlock}>
        <h2>Welcome Back</h2>
        <p>Enter your password to access your account</p>
        <p style={{ fontSize: '0.9em', color: '#666' }}>
          Signing in as: <strong>{email}</strong>&nbsp;
          <Link
            to="#"
            onClick={onBackToEmail}
            style={{ textDecoration: 'underline' }}
          >
            (change)
          </Link>
        </p>
      </div>

      {backendError && (
        <div className={styles.notification}>
          <Notification
            type="error"
            message={
              <>
                We couldn’t sign you in.&nbsp;
                <Link
                  to="#"
                  onClick={() => {
                    setNeedHelpModalShow(true);
                  }}
                >
                  Need help?
                </Link>
              </>
            }
          />
        </div>
      )}

      <Dialog
        fullWidth
        open={needHelpModalShow}
        onClose={() => {
          setNeedHelpModalShow(false);
        }}
      >
        <DialogContent>
          <Typography variant="h5">Possible reasons:</Typography>
          <ul className={styles.modalUl}>
            <li>Email or password is incorrect</li>
            <li>
              You’re signing in from an IP address that isn’t on your company’s
              allow-list
            </li>
            <li>Your account is locked or disabled</li>
          </ul>
          <>
            Still stuck?&nbsp;
            <Link to="/forgot-password">Reset your password</Link>
            &nbsp;or contact your admin.
          </>
        </DialogContent>
      </Dialog>

      <form onSubmit={submitForm}>
        <input type="hidden" name="email" value={email} />

        <div className={styles.formGroup}>
          <FormGroup
            label="Password"
            type="password"
            name="password"
            icon={<PasswordIcon />}
            error={passwordError}
          />
        </div>

        {showTrustDevice && (
          <div className={styles.checkboxGroup}>
            <label>
              <input
                type="checkbox"
                name="trust_device"
                defaultChecked={document.cookie.includes('trust_device')}
              />
              &nbsp;Trust this device
            </label>
          </div>
        )}

        <div className={styles.button}>
          <Button text="Log In" submit />
        </div>

        {showForgotPassword && (
          <div className={styles.forgotLink}>
            <Link to="/forgot-password" className={styles.link}>
              Forgot Password?
            </Link>
          </div>
        )}
      </form>
    </div>
  );
}

BasicSignIn.propTypes = {
  submit: PropTypes.func.isRequired,
  warning: PropTypes.string,
  email: PropTypes.string.isRequired,
  onBackToEmail: PropTypes.func.isRequired,
  showForgotPassword: PropTypes.bool,
  showTrustDevice: PropTypes.bool,
};

BasicSignIn.defaultProps = {
  showForgotPassword: true,
  showTrustDevice: true,
};

export default BasicSignIn;
